import React, { useState } from "react";
import { useTranslation } from "shared/contexts/I18nContext";
import { useLoading } from "shared/contexts/Loading";
import ContentHeaderSection from "../../shared/common/ContentHeaderSection";
import KebabMenu from "shared/common/KebabMenu";
import apiCall from "shared/services/ApiAdapter";
import { mockAssignments } from "shared/services/mockData";
import useBatchEvaluations from "shared/hooks/useBatchEvaluations";

const BatchEvaluationsContent = ({
  navigateTo,
  selectedCourseTitle,
  selectedCourseId,
  selectedBatchId,
  useMockData,
  displayToast,
}) => {
  const { t } = useTranslation();
  const { startLoading, stopLoading } = useLoading();
  // Fallback to session storage if batch id is not provided via props
  const storedBatchId = (() => {
    try {
      return sessionStorage.getItem("app_selected_batch_id");
    } catch (e) {
      return null;
    }
  })();
  const batchIdForHook = selectedBatchId || storedBatchId || null;
  const storedCourseId = (() => {
    try {
      return sessionStorage.getItem("app_selected_course_id");
    } catch (e) {
      return null;
    }
  })();
  const courseIdForHook = selectedCourseId || storedCourseId || undefined;

  const { evaluations, refetch } = useBatchEvaluations(
    batchIdForHook,
    courseIdForHook
  );

  // Fetching handled by hook

  const handleRevokeEvaluation = async (evaluationId) => {
    try {
      // Remove all assignments for this evaluation in this batch
      const extractIdFromLink = (link) => {
        if (!link) return null;
        const last = String(link).split("/").filter(Boolean).pop();
        const digits = last.replace(/\D/g, "");
        return digits.length ? digits : last;
      };

      const assignmentsToRemove = mockAssignments.filter((assignment) => {
        const evalIdFromObj = assignment?.evaluation?.id;
        const evalIdFromLink = extractIdFromLink(assignment?.evaluationLink);
        const batchIdFromObj = assignment?.batch?.id;
        return (
          (String(evalIdFromObj) === String(evaluationId) ||
            String(evalIdFromLink) === String(evaluationId)) &&
          String(batchIdFromObj) === String(selectedBatchId)
        );
      });

      // Remove assignments from mock data
      assignmentsToRemove.forEach((assignment) => {
        const index = mockAssignments.findIndex((a) => a.id === assignment.id);
        if (index > -1) {
          mockAssignments.splice(index, 1);
        }
      });

      displayToast("Evaluation revoked successfully", "success");

      // Refresh the evaluations list
      await refetch();
    } catch (error) {
      console.error("Failed to revoke evaluation:", error);
      displayToast("Failed to revoke evaluation", "error");
    }
  };

  const rightSideContentTop = (
    <>
      <div className="relative w-full max-w-40">
        <input
          type="text"
          placeholder={t("search")}
          className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600 text-gray-900"
        />
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
          🔍
        </span>
      </div>
      <div className="flex items-center justify-center w-10 h-10 text-xl text-gray-100 bg-gray-800 rounded-full cursor-pointer dark:bg-gray-600 dark:text-gray-200">
        ⚙️
      </div>
    </>
  );

  return (
    <div className="p-8 md:px-10">
      <ContentHeaderSection
        title={
          <div className="flex items-center gap-2">
            <button
              onClick={() =>
                navigateTo("myBatches", selectedCourseTitle, selectedCourseId)
              }
              className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none"
              aria-label="Go back to batches"
            >
              <svg
                className="w-6 h-6 text-gray-600 dark:text-gray-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                ></path>
              </svg>
            </button>
            <span className="text-2xl font-semibold text-gray-900 dark:text-gray-100 tracking-tight leading-tight">
              {t("batchEvaluations")}
            </span>
          </div>
        }
        subtitle={t("manageBatchEvaluations")}
        rightContentTop={rightSideContentTop}
        rightContentBottom={null}
      />

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {evaluations.length === 0 ? (
          <div className="col-span-full text-center text-gray-600 dark:text-gray-400">
            {t("noBatchEvaluationsFound")}
          </div>
        ) : (
          evaluations.map((evaluation) => (
            <div
              key={evaluation.id}
              className="relative flex flex-col items-start p-6 bg-white rounded-xl shadow-lg dark:bg-gray-800 gap-3 cursor-pointer hover:shadow-xl transition-shadow duration-200"
              onClick={() => {
                try {
                  if (selectedBatchId) {
                    sessionStorage.setItem(
                      "app_selected_batch_id",
                      String(selectedBatchId)
                    );
                  }
                } catch (e) {}
                navigateTo(
                  "evaluationAssignments",
                  selectedBatchId,
                  selectedCourseTitle,
                  evaluation.id
                );
              }}
            >
              <div className="absolute top-4 right-4">
                <KebabMenu
                  onRevoke={() => handleRevokeEvaluation(evaluation.id)}
                  revokeLabel="Revoke Evaluation"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 leading-tight">
                {evaluation.name || evaluation.chapter || t("evaluation")}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {evaluation.conceptTopic || "-"} | {evaluation.difficulty}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {evaluation.numberOfQuestions} Qs • {evaluation.maxTimeMinutes}{" "}
                mins
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Due: {evaluation.expiryDateTime?.split("T")[0]}
              </p>
              {evaluation.isPractice && (
                <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full dark:bg-green-800 dark:text-green-100">
                  Practice
                </span>
              )}
              <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {t("clickToViewAssignments")}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default BatchEvaluationsContent;
